import {
  getLocalStorageJson,
  removeItemFromLocalStorage,
  setLocalStorageJson,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { getShopifySessionId } from "@redotech/redo-web/utils/shopify-session";

interface CacheData {
  sessionId: string;
  viewedProducts: SaveForLaterProductData[];
  hasResolvedSlideInPanel: boolean;
}

export interface SaveForLaterProductData {
  productId: string;
  productHandle: string;
  productVariantId: string | undefined;
  productImgSrc?: string | undefined;
}

class _SaveForLaterCache {
  static STORAGE_KEY = "redo.save_for_later_cache";

  shouldTrackSaveForLater(): boolean {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );
    const shopifySessionId = getShopifySessionId();

    return (
      !cacheData ||
      cacheData.sessionId !== shopifySessionId ||
      !cacheData.hasResolvedSlideInPanel
    );
  }

  /**
   * Slide in panel is considered resolved when:
   * (1) the user has completed the save for later steps (i.e. login/signup) OR
   * (2) the user has dismissed the panel
   */
  hasResolvedSlideInPanel(): boolean {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    return !!cacheData?.hasResolvedSlideInPanel;
  }

  handleResolvedSlideInPanel(): void {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    if (cacheData) {
      const updatedCacheData: CacheData = {
        ...cacheData,
        hasResolvedSlideInPanel: true,
      };
      setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, updatedCacheData);
    }
  }

  getAllProductsToSaveForLater(): SaveForLaterProductData[] {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    if (!cacheData?.viewedProducts) {
      return [];
    }

    return cacheData.viewedProducts;
  }

  /**
   * Adds a product to the save for later cache if a product with the same id
   * is not already in the cache.
   */
  addProductToSaveForLaterCache(
    product: SaveForLaterProductData,
  ): SaveForLaterProductData[] {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );
    const shopifySessionId = getShopifySessionId();

    if (this.shouldTrackSaveForLater()) {
      if (!cacheData || cacheData.sessionId !== shopifySessionId) {
        // If there is no cache or the user is in a new session, create a new cache.
        setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, {
          sessionId: shopifySessionId,
          viewedProducts: [product],
          hasResolvedSlideInPanel: false,
        });
      } else if (
        !cacheData.viewedProducts.some((p) => p.productId === product.productId)
      ) {
        // Otherwise, if the product (excluding variants) is not already in the cache,
        // add the product to the cache.
        const updatedCacheData: CacheData = {
          sessionId: cacheData.sessionId,
          viewedProducts: [...cacheData.viewedProducts, product],
          hasResolvedSlideInPanel: false,
        };
        setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, updatedCacheData);
      }
    }

    return this.getAllProductsToSaveForLater();
  }

  clear() {
    removeItemFromLocalStorage(_SaveForLaterCache.STORAGE_KEY);
  }
}

export const SaveForLaterCache = new _SaveForLaterCache();
